import { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import { Block } from "@mui/icons-material";
import { useRecordContext, useNotify, useRefresh } from "react-admin";
import { handleApiError } from "../../../utils/errorHandler";
import { ProviderDataProvider } from "../../../dataProviders/providerProvider";
import type { Environment } from "../../../types/api";

/**
 * Replaces react-admin's generic <DeleteButton /> for providers — providers
 * are code-registered, not DB rows, so "delete" really means "deactivate one
 * environment's business-rules row" (DELETE /admin/providers/:code?environment=).
 * The generic DeleteButton has no way to pick which environment, so it
 * always hit production; this asks first.
 */
export const ProviderDeactivateAction = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [environment, setEnvironment] = useState<Environment>("production");
  const [loading, setLoading] = useState(false);

  if (!record) return null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await ProviderDataProvider.deactivateProvider(
        String(record.id),
        environment,
      );
      notify(`Provider deactivated (${environment})`, { type: "success" });
      setOpen(false);
      refresh();
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        startIcon={<Block />}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        size="small"
        color="error"
      >
        Deactivate
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Deactivate Provider</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Choose which environment&apos;s business rules to mark inactive.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Environment</InputLabel>
              <Select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as Environment)}
                label="Environment"
              >
                <MenuItem value="production">Production</MenuItem>
                <MenuItem value="development">Development</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? "Deactivating..." : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
